import InfoCircle from '@/assets/icons/info-circle.svg?react';
import { Card, Tooltip, Carousel } from 'antd';
import styles from './maintenance-card.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Icon from '@ant-design/icons';
import MaintenanceStatusChip from '../maintenance-status-chip/maintenance-status-chip';
import PriorityChip from '../priority-chip/priority-chip';
import { Maintenance } from '@/services/maintenances-service/maintenances-service.types';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import { getDynamicMaxLength } from './maintenance-card.utils';

interface MaintenanceCardProps {
  maintenances?: Maintenance[];
  className?: string;
  carouselClassName?: string;
}

export function MaintenanceCard({
  maintenances = [],
  className,
  carouselClassName,
}: MaintenanceCardProps) {
  const { t } = useTranslation('maintenances');
  const { width } = useScreenSize();

  return (
    <Carousel
      arrows={maintenances.length > 1}
      infinite={true}
      className={clsx(styles.carousel, carouselClassName)}
    >
      {maintenances.map((maintenance) => {
        const dynamicMaxLength = getDynamicMaxLength(width);

        const isLongDescription =
          maintenance.description && maintenance.description.length > dynamicMaxLength;
        const truncatedSummary = isLongDescription
          ? `${maintenance.description.slice(0, dynamicMaxLength)}...`
          : maintenance.description;

        return (
          <div key={maintenance.id}>
            <Card className={clsx(styles.maintenanceCard, className)} key={maintenance.id}>
              <p className={styles.title}>{t('card.title')}</p>
              {maintenance.description && (
                <Tooltip
                  title={isLongDescription ? maintenance.description : null}
                  className={styles.tooltip}
                >
                  <p className={styles.maintenanceCardDescription}>{truncatedSummary}</p>
                  <div>
                    {isLongDescription && (
                      <Icon
                        component={() => <InfoCircle fill="none" height="12" width="12" />}
                        className={styles.tooltipIcon}
                      />
                    )}
                  </div>
                </Tooltip>
              )}

              <div className={styles.maintenanceChips}>
                <div className={styles.maintenanceStatusChip}>
                  <span className={styles.maintenanceStatusText}>{t('fields.status')}</span>
                  {maintenance.status && <MaintenanceStatusChip status={maintenance.status} />}
                </div>

                <div className={styles.maintenancePriorityChip}>
                  <span className={styles.maintenancePriorityText}>{t('fields.priority')}</span>
                  {maintenance.priority && <PriorityChip priority={maintenance.priority} />}
                </div>
              </div>
            </Card>
          </div>
        );
      })}
    </Carousel>
  );
}
